import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ComparisonTableData,
  ComparisonTableHeader
} from '@bo-client/shared/components/comparison-table/comparison-table.component';
import {
  MastrLocationDto,
  MastrPvPlantWithLocationDto
} from '@bo-client/activation-management/shared/components/mastr-validation/MastrData.dto';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap
} from 'rxjs/operators';
import { Observe } from '@bo-client/shared/observe.util';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { NBPConfig } from '@bo-client/shared/config.model';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { RegexToCheckModuleMaStrNumber } from '@bo-client/shared/forms/forms.validators';
import { CompanyLocation } from '@bo-client/shared/models/companyLocations';

/**
 * Eintrag in `mastrDataMismatchFields`, der dem Parent-Formular signalisiert,
 * dass die MaStR-Nummer selbst (noch) nicht gültig ist – z.B. weil sie leer ist,
 * dem Pflichtmuster nicht entspricht, gerade geladen wird oder die Daten nicht
 * abrufbar sind. Im Erfolgsfall taucht dieser Eintrag NICHT mehr im Output auf.
 */
export const MASTR_NUMBER_MISMATCH_TAG = 'mastrNumber';

/**
 * Lebenszyklus der MaStR-Nummer‐Validierung, den die Komponente an ihre Umgebung
 * (z.B. das Inbetriebsetzungsformular) kommuniziert. `'valid'` ist der einzige
 * Status, in dem das Parent-Formular eine Absendung allein anhand seiner
 * eigenen Validatoren und der Datenvergleichstabelle entscheiden darf.
 */
export type MastrNumberStatus = 'pattern-mismatch' | 'loading' | 'invalid' | 'valid';

@Component({
  selector: 'app-mastr-validation',
  templateUrl: './mastr-validation.component.html',
  styleUrl: './mastr-validation.component.scss'
})
export class MastrValidationComponent implements OnInit {
  /** Datensatz der Netzanmeldung */
  @Input() grData: GridRegistrationModel | FeederContractModel;
  @Observe('grData') grData$!: Observable<GridRegistrationModel | FeederContractModel>;
  /** Nummer im Marktstammdatenregister */
  @Input() mastrNumber: string;
  @Observe('mastrNumber') mastrNumber$: Observable<string>;

  @Input() companyLocationExternalId: CompanyLocation['external_id'];
  @Observe('companyLocationExternalId') companyLocationExternalId$: Observable<
    CompanyLocation['external_id']
  >;

  /** Use storage-unit MaStR API and (later) storage-specific comparison rows. */
  @Input() mastrFetchMode: 'producerPv' | 'storageUnit' = 'producerPv';
  @Observe('mastrFetchMode') mastrFetchMode$!: Observable<'producerPv' | 'storageUnit'>;

  @Output() mastrDataMismatchFields = new EventEmitter<string[]>();
  @Output() mastrDataFetch = new EventEmitter<MastrPvPlantWithLocationDto>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly httpHapService = inject(HttpHapService);
  private readonly dataShareService = inject(DataShareService);

  /**
   * Aktueller Status der MaStR-Nummer-Validierung. Das Parent-Formular nutzt
   * diesen Wert (indirekt via `mastrDataMismatchFields`) um die Abgabe zu blockieren,
   * solange noch keine validen Daten vorliegen.
   */
  private readonly _mastrNumberStatus$ = new BehaviorSubject<MastrNumberStatus>('pattern-mismatch');
  readonly mastrNumberStatus$ = this._mastrNumberStatus$.asObservable();

  /** Aktuell letzte vom Vergleich erkannte Datenfeld-Abweichungen (ohne Sentinel). */
  private lastDataMismatchFields: string[] = [];

  /** Funktionen, zum Mappen der Netzanmeldungsdaten,
   * um sie in das Format vom Marktstammdatenregister zu bekommen*/
  private readonly dataMapUtil = {
    gridRegistrationModel: {
      getGrModuleNumber(data: GridRegistrationModel | FeederContractModel) {
        return data.pvModules.map((item) => item.count).reduce((acc, cur) => acc + cur, 0);
      },
      getInstalledPower(data: GridRegistrationModel | FeederContractModel) {
        return data.producers
          .map((item) => item.count * item.acActivePowerMax)
          .reduce((acc, cur) => acc + cur, 0);
      },
      getBruttoPower(data: GridRegistrationModel | FeederContractModel) {
        return (
          data.pvModules.map((item) => item.count * item.modulePowerWatt).reduce((acc, cur) => acc + cur, 0) /
          1000
        );
      },
      getCustomerData: (key: keyof (GridRegistrationModel | FeederContractModel)['customer']) => {
        return (data: GridRegistrationModel | FeederContractModel) => (data.customer || {})[key];
      },
      getConnectionObjectData: (
        key: keyof (GridRegistrationModel | FeederContractModel)['connectionObject']
      ) => {
        return (data: GridRegistrationModel | FeederContractModel) => {
          return (data.connectionObject || {})[key];
        };
      }
    },
    mastrDataModel: {
      getConnectionObjectData: (key: keyof MastrLocationDto) => {
        return (data: MastrPvPlantWithLocationDto) => (data.location || {})[key];
      }
    }
  };

  /** Comparison rows for storage-unit validation (empty scaffolding; use dataMapUtil + getters when defined). */
  private readonly dataGettersStorage: DataGetter[] = [];

  /** Gibt ein Schema für die Daten an, die in der Vergleichstabelle angezeigt werden sollen.
   *
   * Enthält das Label der Datensätze sowie die Getter-Funktionen oder Schlüssel,
   * um die Daten aus dem GridRegistrationModel und dem MastrPvPlantWithLocationDto zu extrahieren.
   */
  private readonly dataGetters: DataGetter[] = [
    {
      label: 'AC-maximale Wirkleistung (gesamt)',
      formFieldName: 'newProduction',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getInstalledPower,
      mastrDataGetter: (data) => data.pvUnit?.zugeordneteWirkleistungWechselrichter
    },
    {
      label: 'Anzahl der Module',
      formFieldName: 'pvModules',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getGrModuleNumber,
      mastrDataGetter: (data) => data.pvUnit?.anzahlModule
    },
    {
      label: 'Gesamtleistung der PV-Module',
      formFieldName: 'newInstalledPower',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getBruttoPower,
      mastrDataGetter: (data) => data.pvUnit?.installierteLeistung
    },
    {
      label: 'PLZ Anschlussobjekt',
      formFieldName: 'customer.zipCode',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('zipCode'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('postleitzahl'),
      warnOnly: true
    },
    {
      label: 'Ort Anschlussobjekt',
      formFieldName: 'customer.city',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('city'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('ort'),
      warnOnly: true
    },
    {
      label: 'Straße Anschlussobjekt',
      formFieldName: 'customer.street',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('street'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('strasse'),
      warnOnly: true
    },
    {
      label: 'HausNr Anschlussobjekt',
      formFieldName: 'customer.streetNo',
      grDataGetter: this.dataMapUtil.gridRegistrationModel.getConnectionObjectData('streetNo'),
      mastrDataGetter: this.dataMapUtil.mastrDataModel.getConnectionObjectData('hausnummerWert'),
      warnOnly: true
    },
    {
      label: 'Speicherkapazität',
      formFieldName: 'storages[0].usableCapacity',
      grDataGetter: (data) =>
        (!!data.storages.length && (data.storages[0].usableCapacity || 0) * (data.storages[0].count || 0)) ||
        '',
      mastrDataGetter: (data) =>
        (!!data.location?.storageUnits?.length && data.location.storageUnits[0].nutzbareSpeicherkapazitaet) ||
        '',
      show: (model, pvPlant) => !!pvPlant.location.storageUnits.length
    }
  ];
  readonly tableHeader: ComparisonTableHeader = {
    labelHeader: 'Beschreibung',
    firstDataHeader: 'Angabe im Portal',
    secondDataHeader: 'Angabe im Marktstammdatenregister'
  };
  _data$: BehaviorSubject<ComparisonTableData[]> = new BehaviorSubject([]);
  data$ = this._data$.asObservable();

  private _error$ = new BehaviorSubject<{ title: string; message: string } | null>(null);
  error$ = this._error$.asObservable();

  private emptyComparisonData(): ComparisonTableData[] {
    return [
      {
        label: 'Keine Daten verfügbar',
        firstDataPoint: '—',
        secondDataPoint: '—'
      }
    ];
  }

  private mapToComparisonData(
    pvPlant: MastrPvPlantWithLocationDto,
    model: GridRegistrationModel | FeederContractModel,
    config: NBPConfig,
    fetchMode: 'producerPv' | 'storageUnit' = 'producerPv'
  ): ComparisonTableData[] {
    const getters = fetchMode === 'storageUnit' ? this.dataGettersStorage : this.dataGetters;
    return getters.map((getter) => {
      let allowedDeviation: number;
      switch (getter.formFieldName) {
        case 'newInstalledPower': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'newProduction': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedPowerDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'storages[0].usableCapacity': {
          const deviation = (
            config.gridRegistration?.mastrValidationConfig?.allowedCapacityDeviation || '0'
          ).replace(',', '.');
          allowedDeviation = Number(deviation);
          break;
        }
        case 'pvModules': {
          const deviation =
            // Regex prüft auf Komma und Punkt als Dezimaltrennzeichen
            config.gridRegistration?.mastrValidationConfig?.allowedModuleCountDeviation.split(/[,.]/)[0];
          allowedDeviation = Number(deviation);
          break;
        }
        default: {
          break;
        }
      }
      const strictAddressValidation =
        config.gridRegistration?.mastrValidationConfig?.enableStrictAddressValidation || false;
      return {
        label: getter.label,
        warnOnly:
          getter.formFieldName?.startsWith('customer.') && strictAddressValidation ? false : getter.warnOnly,
        allowedDeviation: allowedDeviation || undefined,
        firstDataPoint:
          typeof getter.grDataGetter === 'function'
            ? getter.grDataGetter.call(this, model)
            : model[getter.grDataGetter],
        secondDataPoint:
          typeof getter.mastrDataGetter === 'function'
            ? getter.mastrDataGetter.call(this, pvPlant)
            : pvPlant[getter.mastrDataGetter]
      };
    });
  }

  ngOnInit(): void {
    this.mapData();
  }

  private mapData() {
    this.mastrNumber$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        combineLatestWith(this.companyLocationExternalId$, this.mastrFetchMode$),
        distinctUntilChanged((previous, current) => {
          return previous[0] === current[0] && previous[1] === current[1] && previous[2] === current[2];
        }),
        tap(() => {
          // Beim Wechsel der Eingabe alte Vergleichsdaten leeren und den Parent-Status zurücksetzen.
          this.lastDataMismatchFields = [];
          this._data$.next([]);
          this._error$.next(null);
        }),
        filter(([mastrNumber, , mode]) => this.checkValidity(mastrNumber, mode ?? 'producerPv')),
        tap(() => {
          // Pattern hat gepasst → Daten werden geladen, Formular muss währenddessen blockiert bleiben.
          this.setMastrNumberStatus('loading');
        }),
        switchMap(([mastrNr, companyExternalId, mode]) => {
          const fetchMode = mode ?? 'producerPv';
          const basePath =
            fetchMode === 'storageUnit'
              ? `/external/mastr-data-storage/${mastrNr}`
              : `/external/mastr-data/${mastrNr}`;
          const url = companyExternalId ? `${basePath}?externalId=${companyExternalId}` : basePath;
          return this.httpHapService.getTyped<MastrPvPlantWithLocationDto>(url).pipe(
            tap((response) => {
              this.mastrDataFetch.emit(response);
              this.setMastrNumberStatus('valid');
            }),
            combineLatestWith(this.grData$, this.dataShareService.tenantConfig$),
            map(([pvPlant, grData, config]) => this.mapToComparisonData(pvPlant, grData, config, fetchMode)),
            catchError((err) => {
              // eslint-disable-next-line no-console
              console.error('Fehler beim Mastr‑Abruf', err);
              this._error$.next({
                title: 'Fehler beim Abrufen der Daten',
                message: 'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden.'
              });
              this.setMastrNumberStatus('invalid');
              return of(this.emptyComparisonData()); // Stream bleibt lebendig
            })
          );
        }),
        // **alle übrigen** Fehler abfangen (z. Tippfehler in mapToComparisonData)
        catchError((err) => {
          // eslint-disable-next-line no-console
          console.error('Unerwarteter Fehler in der Pipeline', err);
          this._error$.next({
            title: 'Unbekannter Fehler',
            message: 'Es ist ein unerwarteter Fehler aufgetreten.'
          });
          this.setMastrNumberStatus('invalid');
          return of(this.emptyComparisonData());
        }),
        shareReplay(1)
      )
      .subscribe((data) => {
        this._data$.next(data);
      });
  }

  /**
   * Aktualisiert den Status der MaStR-Nummer-Prüfung und sendet den passenden
   * `mastrDataMismatchFields`-Output an die Parent-Komponente. Solange der Status
   * nicht `'valid'` ist, wird ein Sentinel-Eintrag {@link MASTR_NUMBER_MISMATCH_TAG}
   * vorne angefügt, damit das Formular die Abgabe blockiert.
   */
  private setMastrNumberStatus(status: MastrNumberStatus): void {
    this._mastrNumberStatus$.next(status);
    this.emitMismatchFields();
  }

  private emitMismatchFields(): void {
    const status = this._mastrNumberStatus$.getValue();
    if (status === 'valid') {
      this.mastrDataMismatchFields.emit([...this.lastDataMismatchFields]);
    } else {
      this.mastrDataMismatchFields.emit([MASTR_NUMBER_MISMATCH_TAG]);
    }
  }

  handleFieldMismatch(labels: string[]): void {
    this.lastDataMismatchFields = labels.map((label) => {
      const getter = this.dataGetters.find((g) => g.label === label);
      if (!getter) {
        // eslint-disable-next-line no-console
        console.warn(`Kein Getter für Label "${label}" gefunden.`);
        return label; // Fallback auf das Label selbst
      }
      if (getter.formFieldName) return getter.formFieldName;
      if (typeof getter.grDataGetter === 'string') return getter.grDataGetter;
      return label;
    });
    this.emitMismatchFields();
  }

  checkValidity = (mastrNumber: string, mode: 'producerPv' | 'storageUnit' = 'producerPv'): boolean => {
    if (!mastrNumber?.length) {
      this._error$.next({
        title: 'Keine Marktstammdatenregister-Nummer angegeben',
        message:
          'Bitte geben Sie eine gültige Marktstammdatenregister-Nummer ein, um eine Datenvalidierung zu erhalten'
      });
      this.setMastrNumberStatus('pattern-mismatch');
      return false;
    }
    if (mode === 'storageUnit') {
      // TODO: add format check for Stromspeichereinheit MaStR numbers when specified
      return true;
    }
    if (!RegexToCheckModuleMaStrNumber.test(mastrNumber)) {
      this._error$.next({
        title: 'Ungültige Marktstammdatenregister-Nummer',
        message:
          'Die eingegebene Marktstammdatenregister-Nummer entspricht keinem gültigen Format. Bitte überprüfen Sie die Eingabe. (Beginnend mit "SEE9", gefolgt von 11 weiteren Ziffern)'
      });
      this.setMastrNumberStatus('pattern-mismatch');
      return false;
    }
    return true;
  };
}

type DataGetter =
  | {
      label: string;
      formFieldName?: string; // Optional, falls der Getter nicht dem Formfeldnamen entspricht
      grDataGetter: keyof (GridRegistrationModel | FeederContractModel);
      mastrDataGetter: keyof MastrPvPlantWithLocationDto | ((data: MastrPvPlantWithLocationDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
      show?: (
        model: GridRegistrationModel | FeederContractModel,
        pvPlant: MastrPvPlantWithLocationDto
      ) => boolean;
    }
  | {
      label: string;
      formFieldName: string; // Um einen Errortext zu generieren, wenn die Daten nicht übereinstimmen
      grDataGetter: (data: GridRegistrationModel | FeederContractModel) => unknown;
      mastrDataGetter: keyof MastrPvPlantWithLocationDto | ((data: MastrPvPlantWithLocationDto) => unknown);
      allowedDeviation?: number;
      warnOnly?: boolean;
      show?: (
        model: GridRegistrationModel | FeederContractModel,
        pvPlant: MastrPvPlantWithLocationDto
      ) => boolean;
    };
